import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Plus, 
  Copy, 
  Trash2, 
  Eye,
  Search,
  Calendar,
  Zap,
  Key as KeyIcon,
  Edit
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/services/api'
import { formatNumber, formatDate, formatDateTime } from '@/lib/utils'

interface ApiKey {
  id: string
  name: string
  key: string
  createdAt: string
  lastUsed?: string
  requestCount: number
  isActive: boolean
}

export function ApiKeysPage() {
  const { toast } = useToast()
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    fetchApiKeys()
  }, [])

  const fetchApiKeys = async () => {
    try {
      const response = await api.getApiKeys()
      setApiKeys(response.data)
    } catch (error: any) {
      toast({
        title: 'Error loading API keys',
        description: error.message || 'Failed to load API keys',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast({
        title: 'Validation error',
        description: 'Please enter a name for your API key',
        variant: 'destructive',
      })
      return
    }

    setCreating(true)
    try {
      const response = await api.createApiKey({ name: newKeyName })
      setApiKeys([response.data, ...apiKeys])
      setNewKeyName('')
      setShowCreateModal(false)
      toast({
        title: 'API key created',
        description: 'Your new API key has been created successfully',
      })
    } catch (error: any) {
      toast({
        title: 'Create failed',
        description: error.message || 'Failed to create API key',
        variant: 'destructive',
      })
    } finally {
      setCreating(false)
    }
  }

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key)
      toast({
        title: 'API key copied',
        description: 'API key has been copied to clipboard',
      })
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy API key to clipboard',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteKey = async (keyId: string) => {
    try {
      await api.deleteApiKey(keyId)
      setApiKeys(apiKeys.filter(key => key.id !== keyId))
      toast({
        title: 'API key deleted',
        description: 'API key has been successfully deleted',
      })
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: error.message || 'Failed to delete API key',
        variant: 'destructive',
      })
    }
  }

  const filteredApiKeys = apiKeys.filter(key =>
    key.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Keys</h1>
          <p className="text-muted-foreground">
            Manage your API keys and track their usage
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create API Key
        </Button>
      </div>

      {/* Search Bar */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search API keys..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* API Keys List */}
      <div className="space-y-4">
        {filteredApiKeys.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <KeyIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">
                {searchTerm ? 'No matching API keys found' : 'No API keys yet'}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {searchTerm 
                  ? 'Try adjusting your search terms.'
                  : 'Get started by creating your first API key.'
                }
              </p>
              {!searchTerm && (
                <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create API Key
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredApiKeys.map((apiKey, index) => (
            <motion.div
              key={apiKey.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-lg ${
                        apiKey.isActive 
                          ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400' 
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        <KeyIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{apiKey.name}</h3>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center">
                            <Calendar className="mr-1 h-3 w-3" />
                            Created: {formatDate(apiKey.createdAt)}
                          </span>
                          {apiKey.lastUsed && (
                            <span className="flex items-center">
                              <Eye className="mr-1 h-3 w-3" />
                              Last used: {formatDateTime(apiKey.lastUsed)}
                            </span>
                          )}
                          <span className="flex items-center">
                            <Zap className="mr-1 h-3 w-3" />
                            {formatNumber(apiKey.requestCount)} requests
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-right">
                        <div className={`text-xs font-medium px-2 py-1 rounded-full ${
                          apiKey.isActive 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {apiKey.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopyKey(apiKey.key)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteKey(apiKey.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border rounded-lg p-6 w-full max-w-md"
          >
            <h2 className="text-xl font-semibold mb-4">Create New API Key</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="keyName">API Key Name</Label>
                <Input
                  id="keyName"
                  placeholder="Enter a name for your API key"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="mt-1"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Choose a descriptive name to help you identify this key later.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateKey} disabled={creating}>
                {creating ? 'Creating...' : 'Create API Key'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}